import React, { useState, useEffect } from 'react';
import PageHeader from '../components/PageHeader';
import { getMessagingRulesApi, updateMessagingRulesApi, getAdminRolesApi } from '../api/settings.api';
import { apiHandler, unwrapData } from '../utils/apiHandler';

const defaultSettings = {
  quietHours: '9:00 PM to 7:00 AM',
  suppressWindow: '72 hours',
  exclusions: 'Opted-out, suspended, test accounts',
  orgTypes: 'Employer, Church, Campus, Nonprofit, Media',
  verLevels: 'Email, Mobile, Fully Verified',
};

const defaultToggles = {
  'chk-quiet': true,
  'chk-suppress': true,
  'chk-exclusion': true,
  'chk-urgent': true,
  'chk-circle': true,
  'chk-roadside': true,
  'chk-medical': true,
};

const roles = [
  { name: 'Admin', color: '#1D4ED8', access: 'Full', accessClass: 'st-access-full' },
  { name: 'Ops', color: '#7E22CE', access: 'User + send', accessClass: 'st-access-usersend' },
  { name: 'Marketing', color: '#C2410C', access: 'Segments + campaigns', accessClass: 'st-access-segments' },
  { name: 'Partner', color: '#B45309', access: 'Org-only', accessClass: 'st-access-org' },
  { name: 'Analyst', color: '#64748B', access: 'Read-only', accessClass: 'st-access-readonly' },
];

export default function Settings() {
  const [fields, setFields] = useState(defaultSettings);
  const [toggles, setToggles] = useState(defaultToggles);
  const [apiRoles, setApiRoles] = useState([]);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    apiHandler(() => getMessagingRulesApi()).then((r) => {
      if (r.success) {
        const cfg = unwrapData(r) || {};
        const val = (key) => {
          const v = cfg[key];
          if (v == null) return null;
          if (typeof v === 'object' && v.value != null) return String(v.value);
          return String(v);
        };
        setFields((prev) => ({
          ...prev,
          quietHours: val('messaging_quiet_hours') || prev.quietHours,
          suppressWindow: val('suppress_duplicate_sends') || prev.suppressWindow,
          exclusions: val('default_exclusions') || prev.exclusions,
        }));
        setToggles((prev) => ({
          ...prev,
          'chk-quiet': val('messaging_quiet_hours_enabled') !== 'false',
          'chk-suppress': val('suppress_duplicate_sends') !== 'false',
          'chk-exclusion': val('default_exclusions_enabled') !== 'false',
        }));
      }
    });
    apiHandler(() => getAdminRolesApi()).then((r) => {
      if (r.success) {
        const data = unwrapData(r);
        setApiRoles(data?.roles || data || []);
      }
    });
  }, []);

  function showToast(msg, type = 'info') {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2800);
  }

  function toggle(key) {
    setToggles(prev => ({ ...prev, [key]: !prev[key] }));
  }

  function setField(key, val) {
    setFields(prev => ({ ...prev, [key]: val }));
  }

  async function saveSettings() {
    const result = await apiHandler(() =>
      updateMessagingRulesApi({
        quietHours: fields.quietHours,
        suppressDuplicateSends: toggles['chk-suppress'],
        defaultExclusions: fields.exclusions,
      })
    );
    if (result.success) showToast('Settings saved successfully', 'success');
    else showToast(result.message || 'Save failed', 'error');
  }

  function revertSettings() {
    setFields(defaultSettings);
    setToggles(defaultToggles);
    showToast('Settings reverted to defaults', 'info');
  }

  function ToggleRow({ id, label }) {
    return (
      <div className="st-toggle-row" onClick={() => toggle(id)} style={{ cursor: 'pointer' }}>
        <div className={`st-checkbox ${toggles[id] ? 'checked' : ''}`}>
          {toggles[id] && <i className="fas fa-check" />}
        </div>
        <span className="st-toggle-label">{label}</span>
      </div>
    );
  }

  return (
    <>
      <PageHeader title="Settings" icon="fa-gear" trail={[{ label: 'Settings' }]} />

      <div className="st-grid fade-up">

        {/* COL 1: Messaging Rules */}
        <div className="st-card">
          <div className="st-card-header">
            <div className="st-card-icon" style={{ background: '#EFF6FF', color: '#1D4ED8' }}><i className="fas fa-comment-dots" /></div>
            <h2 className="st-card-title">Messaging rules</h2>
          </div>
          <div className="st-card-body">
            <ToggleRow id="chk-quiet" label="Quiet hours enabled" />
            <div className="st-field-group">
              <div className="st-field-label">Quiet hours</div>
              <input type="text" className="st-field-input" value={fields.quietHours} onChange={e => setField('quietHours', e.target.value)} />
            </div>
            <ToggleRow id="chk-suppress" label="Suppress duplicate sends" />
            <div className="st-field-group">
              <div className="st-field-label">Suppression window</div>
              <input type="text" className="st-field-input" value={fields.suppressWindow} onChange={e => setField('suppressWindow', e.target.value)} />
            </div>
            <ToggleRow id="chk-exclusion" label="Default exclusion list" />
            <div className="st-field-group">
              <div className="st-field-label">Exclusions</div>
              <textarea className="st-field-textarea" value={fields.exclusions} onChange={e => setField('exclusions', e.target.value)} />
            </div>
          </div>
        </div>

        {/* COL 2: Roles & Permissions */}
        <div className="st-card">
          <div className="st-card-header">
            <div className="st-card-icon" style={{ background: '#F5F3FF', color: '#7E22CE' }}><i className="fas fa-shield-halved" /></div>
            <h2 className="st-card-title">Roles and permissions</h2>
          </div>
          <div className="st-card-body st-card-body--flush">
            <table className="st-roles-table">
              <thead>
                <tr><th>Role</th><th>Access</th></tr>
              </thead>
              <tbody>
                {(apiRoles.length ? apiRoles : roles).map((r) => (
                  <tr key={r.id || r.name} className="st-role-row">
                    <td>
                      <div className="st-role-name">
                        <div className="st-role-dot" style={{ background: r.color || '#64748B' }} />
                        {r.roleName || r.name}
                      </div>
                    </td>
                    <td>
                      <span className={`st-access ${r.accessClass || 'st-access-readonly'}`}>
                        {r.description || r.access || '—'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="st-roles-footer">
              <button className="st-link-btn" onClick={() => showToast('Role editor opening…', 'info')}>
                <i className="fas fa-pen" />Edit roles
              </button>
              <button className="st-link-btn" onClick={() => showToast('Adding new role…', 'info')}>
                <i className="fas fa-plus" />Add role
              </button>
            </div>
          </div>
        </div>

        {/* COL 3: Product & Taxonomy */}
        <div className="st-card">
          <div className="st-card-header">
            <div className="st-card-icon" style={{ background: '#FFF7ED', color: '#C2410C' }}><i className="fas fa-tags" /></div>
            <h2 className="st-card-title">Product and taxonomy</h2>
          </div>
          <div className="st-card-body">
            <div className="st-section-label">Enabled products</div>
            <ToggleRow id="chk-urgent" label="Urgent Assist enabled" />
            <ToggleRow id="chk-circle" label="Trusted Circle enabled" />
            <ToggleRow id="chk-roadside" label="Roadside Assist enabled" />
            <ToggleRow id="chk-medical" label="Urgent Medical Assist enabled" />

            <div className="st-field-group" style={{ marginTop: 18 }}>
              <div className="st-field-label">Organization types</div>
              <input type="text" className="st-field-input" value={fields.orgTypes} onChange={e => setField('orgTypes', e.target.value)} />
            </div>
            <div className="st-field-group">
              <div className="st-field-label">Verification levels</div>
              <input type="text" className="st-field-input" value={fields.verLevels} onChange={e => setField('verLevels', e.target.value)} />
            </div>
            <div className="st-action-row">
              <button className="action-btn btn-green st-save-btn" onClick={saveSettings}>
                <i className="fas fa-floppy-disk" />Save
              </button>
              <button className="action-btn st-revert-btn" onClick={revertSettings}>
                <i className="fas fa-rotate-left" />Revert
              </button>
            </div>
          </div>
        </div>

      </div>

      {/* Toast */}
      {toast && (
        <div className={`cm-toast show ${toast.type}`}>{toast.msg}</div>
      )}
    </>
  );
}
